import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnnouncer } from '../context/AccessibilityContext';
import './LanguageSelector.css';

interface LanguageSelectorProps {
  className?: string;
  compact?: boolean;
}

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
];

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  compact = false,
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const announcer = useAnnouncer();

  const currentLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(SUPPORTED_LANGUAGES.findIndex(lang => lang.code === i18n.language));
        } else if (focusedIndex >= 0) {
          handleLanguageChange(SUPPORTED_LANGUAGES[focusedIndex].code);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex(prev => (prev + 1) % SUPPORTED_LANGUAGES.length);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(SUPPORTED_LANGUAGES.length - 1);
        } else {
          setFocusedIndex(prev => prev <= 0 ? SUPPORTED_LANGUAGES.length - 1 : prev - 1);
        }
        break;
      case 'Home':
        if (isOpen) {
          event.preventDefault();
          setFocusedIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          event.preventDefault();
          setFocusedIndex(SUPPORTED_LANGUAGES.length - 1);
        }
        break;
    }
  };

  const handleLanguageChange = async (languageCode: string) => {
    try {
      await i18n.changeLanguage(languageCode);
      const selectedLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === languageCode);
      if (selectedLanguage) {
        announcer.announce(
          t('settings.languageChanged', { language: selectedLanguage.nativeName }),
          'polite'
        );
      }
      setIsOpen(false);
      setFocusedIndex(-1);
      buttonRef.current?.focus();
    } catch (error) {
      console.error('Failed to change language:', error);
      announcer.announce(t('common.error'), 'assertive');
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setFocusedIndex(SUPPORTED_LANGUAGES.findIndex(lang => lang.code === i18n.language));
    } else {
      setFocusedIndex(-1);
    }
  };

  return (
    <div 
      className={`language-selector ${className} ${compact ? 'language-selector--compact' : ''}`}
      ref={dropdownRef}
    >
      <button
        ref={buttonRef}
        className="language-selector__button"
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t('settings.selectLanguage')}
        type="button"
      >
        <span className="language-selector__current">
          {compact ? currentLanguage.code.toUpperCase() : currentLanguage.nativeName}
        </span>
        <svg
          className={`language-selector__arrow ${isOpen ? 'language-selector__arrow--open' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6,9 12,15 18,9"></polyline>
        </svg>
      </button>

      {isOpen && (
        <div 
          className="language-selector__dropdown"
          role="listbox"
          aria-label={t('settings.selectLanguage')}
        >
          {SUPPORTED_LANGUAGES.map((language, index) => (
            <button
              key={language.code}
              className={`language-selector__option ${
                language.code === i18n.language ? 'language-selector__option--selected' : ''
              } ${
                index === focusedIndex ? 'language-selector__option--focused' : ''
              }`}
              onClick={() => handleLanguageChange(language.code)}
              onMouseEnter={() => setFocusedIndex(index)}
              role="option"
              aria-selected={language.code === i18n.language}
              type="button"
            >
              <span className="language-selector__option-native">
                {language.nativeName}
              </span>
              {!compact && (
                <span className="language-selector__option-english">
                  {language.name}
                </span>
              )}
              {language.code === i18n.language && (
                <svg
                  className="language-selector__check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20,6 9,17 4,12"></polyline>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};